/**
 * Página pública do portal hotspot.
 * Rota: /portal/:tenantKey/:publicUid
 *
 * Recebe da URL do MikroTik os query params:
 *   link-login-only, link-orig, mac, ip, error
 *
 * Sem AuthWrapper — acesso público.
 */

import React, { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery, useMutation } from "react-query";
import axios from "axios";
import { QueryClientProvider } from "react-query";
import { queryClient } from "@/queryClient";
import type { HotspotPortalCampoDTO, HotspotPortalDTO } from "@webip/shared/src/dto/hotspot";
import { getTenantFromWindowPathname } from "@/lib/tenant";

// ─── Helpers ────────────────────────────────────────────────────────────────

function validateCpf(cpf: string): boolean {
    const digits = cpf.replace(/\D/g, "");
    if (digits.length !== 11 || /^(\d)\1{10}$/.test(digits)) return false;
    let sum = 0;
    for (let i = 0; i < 9; i++) sum += Number(digits[i]) * (10 - i);
    let rest = (sum * 10) % 11;
    if (rest === 10 || rest === 11) rest = 0;
    if (rest !== Number(digits[9])) return false;
    sum = 0;
    for (let i = 0; i < 10; i++) sum += Number(digits[i]) * (11 - i);
    rest = (sum * 10) % 11;
    if (rest === 10 || rest === 11) rest = 0;
    return rest === Number(digits[10]);
}

/**
 * Mensagens que o MikroTik devolve em $(error) quando o login falha (login.html
 * é recarregado e redireciona de volta pra cá com ?error=). Traduz as comuns;
 * o resto é exibido como veio, pra não esconder o motivo.
 */
const MK_ERRORS: [RegExp, string][] = [
    [/invalid username or password/i, "Usuário ou senha inválidos."],
    [/radius server is not responding/i, "Servidor de autenticação não respondeu. Tente novamente em instantes."],
    [/simultaneous session limit/i, "Este usuário já atingiu o limite de sessões simultâneas."],
    [/uptime limit/i, "Este usuário atingiu o limite de tempo de uso."],
    [/no more sessions are allowed/i, "Este usuário já atingiu o limite de sessões."],
    [/already authorizing/i, "Seu acesso já está sendo autorizado. Aguarde alguns segundos e tente novamente."],
    [/did not send challenge response/i, "A rede não está configurada para este tipo de login. Avise o suporte."],
];

/**
 * Depois do POST, o router segura a navegação até o RADIUS responder. Se a
 * resposta não vier nesse prazo, o botão volta para permitir nova tentativa.
 */
const REDIRECT_TIMEOUT_MS = 20_000;

function traduzErroMikrotik(raw: string | null): string | null {
    if (!raw) return null;
    for (const [re, msg] of MK_ERRORS) if (re.test(raw)) return msg;
    return raw;
}

/**
 * O login passa pelo MikroTik (redirect pra link-login-only) e, se falhar, o
 * cliente volta pra cá numa página nova — o estado React se perde. Guardamos o
 * que foi digitado (menos senhas) só pelo tempo desse round-trip.
 */
function draftKey(publicUid: string) {
    return `hotspot-portal:draft:${publicUid}`;
}

function saveDraft(publicUid: string, campos: HotspotPortalCampoDTO[], formData: Record<string, string>) {
    try {
        const safe: Record<string, string> = {};
        for (const c of campos) {
            if (c.input_type !== "password" && formData[c.nome] != null) safe[c.nome] = formData[c.nome];
        }
        sessionStorage.setItem(draftKey(publicUid), JSON.stringify(safe));
    } catch {
        // sessionStorage indisponível (modo privado, etc.) — segue sem rascunho
    }
}

function takeDraft(publicUid: string): Record<string, string> {
    try {
        const raw = sessionStorage.getItem(draftKey(publicUid));
        sessionStorage.removeItem(draftKey(publicUid));
        return raw ? (JSON.parse(raw) as Record<string, string>) : {};
    } catch {
        return {};
    }
}

/**
 * Campos exibidos no formulário de login, espelhando `camposLogin` do backend:
 * modo autenticação pede só usuário (+ senha quando obrigatória); modo cadastro
 * pede o formulário completo.
 */
function camposLogin(data: HotspotPortalDTO): HotspotPortalCampoDTO[] {
    if (data.modo !== "autenticacao") return data.campos;
    return data.campos.filter((c) => c.papel === "usuario" || (c.tipo === "senha" && c.obrigatorio));
}

function validateTelefone(telefone: string): boolean {
    const digits = telefone.replace(/\D/g, "");
    return digits.length === 10 || digits.length === 11;
}

function validateEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/** Validação client-side pelo formato do campo — espelha `validarValorCampo` do backend. */
function validateFormato(campo: HotspotPortalCampoDTO, val: string): string | null {
    if (!val || campo.tipo !== "texto") return null;
    switch (campo.formato) {
        case "cpf": return validateCpf(val) ? null : "CPF inválido.";
        case "telefone": return validateTelefone(val) ? null : "Telefone inválido.";
        case "email": return validateEmail(val) ? null : "E-mail inválido.";
        default: return null;
    }
}

// ─── Campo de formulário (renderiza input/select/radio/checkbox conforme input_type) ─

function CampoField({
    campo,
    value,
    onChange,
    idPrefix,
    error,
}: {
    campo: HotspotPortalCampoDTO;
    value: string;
    onChange: (value: string) => void;
    idPrefix?: string;
    error?: string;
}) {
    const id = `${idPrefix ?? ""}${campo.nome}`;
    const isCheckbox = campo.input_type === "checkbox";

    // Página pública: as cores vêm do hotspot, não do tema do app. Fundo e
    // texto explícitos para o campo não herdar o tema escuro do dispositivo.
    const fieldStyle: React.CSSProperties = {
        width: "100%",
        boxSizing: "border-box",
        padding: "0.55rem 0.75rem",
        border: error ? "1px solid #d32f2f" : "1px solid #cbd5e1",
        borderRadius: 6,
        fontSize: "1rem",
        outline: "none",
        background: "#fff",
        color: "#222",
        colorScheme: "light",
    };

    return (
        <div style={{ marginBottom: "0.85rem" }}>
            {!isCheckbox && (
                <label
                    id={`${id}-label`}
                    htmlFor={id}
                    style={{ display: "block", fontSize: "0.85rem", marginBottom: "0.25rem", fontWeight: 500 }}
                >
                    {campo.nome}
                    {campo.obrigatorio && (
                        <span style={{ color: "#d32f2f", marginLeft: 3 }}>*</span>
                    )}
                </label>
            )}

            {isCheckbox ? (
                <label
                    htmlFor={id}
                    style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.9rem", cursor: "pointer" }}
                >
                    <input
                        id={id}
                        name={campo.nome}
                        type="checkbox"
                        checked={value === "true"}
                        required={campo.obrigatorio}
                        onChange={(e) => onChange(e.target.checked ? "true" : "false")}
                        style={{ width: "auto" }}
                    />
                    {campo.nome}
                    {campo.obrigatorio && (
                        <span style={{ color: "#d32f2f", marginLeft: 3 }}>*</span>
                    )}
                </label>
            ) : campo.input_type === "select" ? (
                <select
                    id={id}
                    name={campo.nome}
                    required={campo.obrigatorio}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    style={fieldStyle}
                >
                    <option value="">{campo.placeholder || "Selecione…"}</option>
                    {(campo.opcoes ?? []).map((op) => (
                        <option key={op} value={op}>{op}</option>
                    ))}
                </select>
            ) : campo.input_type === "radio" ? (
                // Grupo de radio: `required` em qualquer um do grupo obriga o navegador a
                // exigir uma escolha; o primeiro recebe o `id` do label do campo.
                <div
                    role="radiogroup"
                    aria-labelledby={`${id}-label`}
                    style={{ display: "flex", flexDirection: "column", gap: "0.4rem", fontSize: "0.95rem" }}
                >
                    {(campo.opcoes ?? []).map((op, i) => (
                        <label
                            key={op}
                            style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}
                        >
                            <input
                                id={i === 0 ? id : undefined}
                                name={campo.nome}
                                type="radio"
                                value={op}
                                checked={value === op}
                                required={campo.obrigatorio && i === 0}
                                onChange={() => onChange(op)}
                                style={{ width: "auto" }}
                            />
                            {op}
                        </label>
                    ))}
                </div>
            ) : (
                <input
                    id={id}
                    name={campo.nome}
                    type={campo.input_type}
                    placeholder={campo.placeholder}
                    required={campo.obrigatorio}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    style={fieldStyle}
                />
            )}
            {error && <p style={{ margin: "3px 0 0", fontSize: "0.8rem", color: "#d32f2f" }}>{error}</p>}
        </div>
    );
}

// ─── Checkbox de aceite dos termos de uso (link para o PDF) ────────────────────

function TermosCheckbox({
    termosUrl,
    checked,
    onChange,
    id,
    error,
}: {
    termosUrl: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
    id: string;
    error?: string;
}) {
    return (
        <div style={{ marginBottom: "0.85rem" }}>
            <label
                htmlFor={id}
                style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem", fontSize: "0.85rem", cursor: "pointer" }}
            >
                <input
                    id={id}
                    type="checkbox"
                    checked={checked}
                    required
                    onChange={(e) => onChange(e.target.checked)}
                    style={{ width: "auto", marginTop: "0.15rem" }}
                />
                <span>
                    Li e aceito os{" "}
                    <a href={termosUrl} target="_blank" rel="noopener noreferrer" style={{ color: "inherit", fontWeight: 600 }}>
                        termos de uso
                    </a>
                    <span style={{ color: "#d32f2f", marginLeft: 3 }}>*</span>
                </span>
            </label>
            {error && <p style={{ margin: "3px 0 0", fontSize: "0.8rem", color: "#d32f2f" }}>{error}</p>}
        </div>
    );
}

// ─── Inner component (usa hooks de rota) ────────────────────────────────────

function PortalContent() {
    const { tenantKey: tenantFromParams, publicUid } = useParams<{ tenantKey?: string; publicUid: string }>();
    const [searchParams] = useSearchParams();
    const tenantKey = tenantFromParams ?? getTenantFromWindowPathname() ?? "";

    const linkLoginOnly = searchParams.get("link-login-only") ?? "";
    const linkOrig = searchParams.get("link-orig") ?? "";
    const mac = searchParams.get("mac") ?? "";
    // IP LAN do cliente, injetado pelo MikroTik ($(ip)). É o que o backend
    // precisa para correlacionar a sessão com os eventos de firewall — o
    // X-Forwarded-For chega com o IP público do NAT, não o do cliente.
    const ip = searchParams.get("ip") ?? "";

    const [formData, setFormData] = useState<Record<string, string>>(() => (publicUid ? takeDraft(publicUid) : {}));
    const [aceiteTermos, setAceiteTermos] = useState(false);
    const [redirecionando, setRedirecionando] = useState(false);
    const [serverError, setServerError] = useState<string | null>(() => traduzErroMikrotik(searchParams.get("error")));
    const [cadastroOpen, setCadastroOpen] = useState(false);
    const [cadastroData, setCadastroData] = useState<Record<string, string>>({});
    const [cadastroAceiteTermos, setCadastroAceiteTermos] = useState(false);
    const [cadastroErrors, setCadastroErrors] = useState<Record<string, string>>({});
    const [cadastroSuccess, setCadastroSuccess] = useState(false);

    // Busca configuração do portal
    const { data, isLoading, isError } = useQuery<HotspotPortalDTO>(
        ["portal", tenantKey, publicUid],
        async () => {
            if (!tenantKey || !publicUid) {
                throw new Error("Tenant ou portal inválido");
            }
            const res = await axios.get<{ ok: boolean; result: HotspotPortalDTO }>(
                `/api/portal/${tenantKey}/${publicUid}`,
            );
            return res.data.result;
        },
        { retry: false, refetchOnWindowFocus: false },
    );

    // Submissão do cadastro
    const mutCadastro = useMutation({
        mutationFn: async (payload: Record<string, string>) => {
            if (!tenantKey || !publicUid) {
                throw new Error("Tenant ou portal inválido");
            }
            const res = await axios.post<{ ok: boolean; error?: string; field?: string }>(
                `/api/portal/${tenantKey}/${publicUid}/cadastro`,
                payload,
            );
            return res.data;
        },
        onSuccess: () => {
            setCadastroSuccess(true);
            setCadastroData({});
            setCadastroAceiteTermos(false);
            setCadastroErrors({});
        },
        onError: (err: unknown) => {
            const data = axios.isAxiosError(err) ? err.response?.data : undefined;
            if (data?.field) {
                setCadastroErrors({ [data.field]: data.error ?? "Valor já cadastrado." });
            } else {
                setCadastroErrors({ _global: data?.error ?? "Erro ao realizar cadastro." });
            }
        },
    });

    function handleCadastroChange(nome: string, value: string) {
        setCadastroData((prev) => ({ ...prev, [nome]: value }));
        setCadastroErrors((prev) => { const n = { ...prev }; delete n[nome]; delete n._global; return n; });
    }

    function handleCadastroAceiteTermosChange(checked: boolean) {
        setCadastroAceiteTermos(checked);
        setCadastroErrors((prev) => { const n = { ...prev }; delete n._termos; return n; });
    }

    function validateCadastroForm(): boolean {
        const errs: Record<string, string> = {};
        if (data?.termos_url && !cadastroAceiteTermos) {
            errs._termos = "É preciso aceitar os termos de uso.";
        }
        for (const campo of data?.campos ?? []) {
            if (campo.input_type === "checkbox") {
                if (campo.obrigatorio && cadastroData[campo.nome] !== "true") {
                    errs[campo.nome] = `Campo obrigatório.`;
                }
                continue;
            }
            const val = (cadastroData[campo.nome] ?? "").trim();
            if (campo.obrigatorio && !val) {
                errs[campo.nome] = `Campo obrigatório.`;
            } else {
                const erroFormato = validateFormato(campo, val);
                if (erroFormato) errs[campo.nome] = erroFormato;
            }
        }
        setCadastroErrors(errs);
        return Object.keys(errs).length === 0;
    }

    function handleCadastroSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!validateCadastroForm()) return;
        mutCadastro.mutate({ ...cadastroData, aceite_termos: cadastroAceiteTermos ? "true" : "false" });
    }

    // Submissão do formulário
    const mutLogin = useMutation({
        mutationFn: async (payload: Record<string, string>) => {
            if (!tenantKey || !publicUid) {
                throw new Error("Tenant ou portal inválido");
            }
            const res = await axios.post<{ ok: boolean; redirectUrl?: string; error?: string }>(
                `/api/portal/${tenantKey}/${publicUid}/login`,
                payload,
            );
            return res.data;
        },
        onSuccess: (result) => {
            if (result.ok && result.redirectUrl) {
                if (publicUid && data) saveDraft(publicUid, camposLogin(data), formData);
                setRedirecionando(true);
                setTimeout(() => setRedirecionando(false), REDIRECT_TIMEOUT_MS);
                window.location.href = result.redirectUrl;
            } else {
                setServerError(result.error ?? "Erro ao processar login.");
            }
        },
        onError: (err: unknown) => {
            const msg = axios.isAxiosError(err)
                ? err.response?.data?.error ?? "Erro ao processar login."
                : "Erro ao processar login.";
            setServerError(msg);
        },
    });

    const loginEmAndamento = mutLogin.isLoading || redirecionando;

    function handleChange(nome: string, value: string) {
        setFormData((prev) => ({ ...prev, [nome]: value }));
    }

    function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (loginEmAndamento) return;
        setServerError(null);
        mutLogin.mutate({
            ...formData,
            aceite_termos: aceiteTermos ? "true" : "false",
            link_login_only: linkLoginOnly,
            link_orig: linkOrig,
            mac,
            ip,
        });
    }

    // ── Estados de carregamento ──────────────────────────────────────────────

    if (isLoading) {
        return (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
                <p style={{ color: "#64748b" }}>Carregando portal…</p>
            </div>
        );
    }

    if (isError || !data) {
        return (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
                <p style={{ color: "#c00" }}>Portal não encontrado ou inativo.</p>
            </div>
        );
    }

    // ── Renderização ──────────────────────────────────────────────────────────

    const bg = data.cor_fundo ?? "#f5f5f5";
    const primary = data.cor_primaria ?? "#1976d2";
    const textColor = data.cor_texto ?? "#222";

    return (
        <div
            style={{
                minHeight: "100vh",
                minWidth: '100%',
                backgroundColor: bg,
                backgroundImage: data.fundo_url ? `url(${data.fundo_url})` : undefined,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                backgroundAttachment: "fixed",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: "system-ui, sans-serif",
                color: textColor,
                // Controles nativos (checkbox/radio/select) herdam daqui, não do tema do dispositivo.
                colorScheme: "light",
            }}
        >
            <div
                style={{
                    background: "rgba(255,255,255,0.95)",
                    borderRadius: 12,
                    padding: "2rem",
                    width: "100%",
                    maxWidth: 400,
                    boxShadow: "0 4px 32px rgba(0,0,0,0.15)",
                }}
            >
                {data.logo_url && (
                    <div style={{ textAlign: "center", marginBottom: "1.25rem" }}>
                        <img
                            src={data.logo_url}
                            alt={data.nome}
                            style={{ maxHeight: 72, maxWidth: "100%", objectFit: "contain" }}
                        />
                    </div>
                )}

                <h1 style={{ fontSize: "1.3rem", fontWeight: 700, marginBottom: "0.25rem", color: primary }}>
                    {data.nome}
                </h1>

                {data.descricao && (
                    <p style={{ fontSize: "0.9rem", color: "#64748b", marginBottom: "1rem" }}>
                        {data.descricao}
                    </p>
                )}

                {serverError && (
                    <div
                        style={{
                            background: "#fdecea",
                            border: "1px solid #f5c6c6",
                            borderRadius: 6,
                            padding: "0.6rem 0.9rem",
                            marginBottom: "1rem",
                            fontSize: "0.9rem",
                            color: "#b71c1c",
                        }}
                    >
                        {serverError}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    {camposLogin(data).map((campo) => (
                        <CampoField
                            key={campo.nome}
                            campo={campo}
                            value={formData[campo.nome] ?? ""}
                            onChange={(v) => handleChange(campo.nome, v)}
                        />
                    ))}

                    {/* Em modo autenticação os termos são aceitos no cadastro */}
                    {data.modo === "cadastro" && data.termos_url && (
                        <TermosCheckbox
                            id="aceite-termos"
                            termosUrl={data.termos_url}
                            checked={aceiteTermos}
                            onChange={setAceiteTermos}
                        />
                    )}

                    <button
                        type="submit"
                        disabled={loginEmAndamento}
                        style={{
                            width: "100%",
                            padding: "0.7rem",
                            background: primary,
                            color: "#fff",
                            border: "none",
                            borderRadius: 8,
                            fontSize: "1rem",
                            fontWeight: 600,
                            cursor: loginEmAndamento ? "not-allowed" : "pointer",
                            marginTop: "0.5rem",
                            opacity: loginEmAndamento ? 0.7 : 1,
                        }}
                    >
                        {loginEmAndamento ? "Aguarde…" : "Entrar"}
                    </button>
                </form>

                {data.permite_autocadastro && (
                    <div style={{ textAlign: "center", marginTop: "1rem" }}>
                        <button
                            type="button"
                            onClick={() => { setCadastroOpen(true); setCadastroSuccess(false); setCadastroData({}); setCadastroAceiteTermos(false); setCadastroErrors({}); }}
                            style={{
                                background: "none",
                                border: "none",
                                color: primary,
                                cursor: "pointer",
                                fontSize: "0.9rem",
                                textDecoration: "underline",
                                padding: 0,
                            }}
                        >
                            Cadastre-se
                        </button>
                    </div>
                )}

                {/* ── Modal de cadastro ────────────────────────────────── */}
                {cadastroOpen && (
                    <div
                        style={{
                            position: "fixed", inset: 0,
                            background: "rgba(0,0,0,0.5)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            zIndex: 1000,
                        }}
                        onClick={(e) => { if (e.target === e.currentTarget) setCadastroOpen(false); }}
                    >
                        <div
                            style={{
                                // Mesmo fundo do card de login: cor_texto do hotspot é
                                // pensada para fundo claro, não para o tema do app.
                                background: "rgba(255,255,255,0.97)",
                                color: textColor,
                                borderRadius: 12,
                                padding: "2rem",
                                width: "100%",
                                maxWidth: 400,
                                maxHeight: "90vh",
                                overflowY: "auto",
                                boxShadow: "0 8px 48px rgba(0,0,0,0.25)",
                            }}
                        >
                            <h2 style={{ fontSize: "1.15rem", fontWeight: 700, marginBottom: "1rem", color: primary }}>
                                Cadastre-se
                            </h2>

                            {cadastroSuccess ? (
                                <div>
                                    <div style={{ background: "#e8f5e9", border: "1px solid #c8e6c9", borderRadius: 6, padding: "0.8rem 1rem", color: "#1b5e20", marginBottom: "1rem" }}>
                                        Cadastro realizado com sucesso! Faça login com suas credenciais.
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setCadastroOpen(false)}
                                        style={{ width: "100%", padding: "0.7rem", background: primary, color: "#fff", border: "none", borderRadius: 8, fontSize: "1rem", fontWeight: 600, cursor: "pointer" }}
                                    >
                                        Ir para o login
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={handleCadastroSubmit}>
                                    {cadastroErrors._global && (
                                        <div style={{ background: "#fdecea", border: "1px solid #f5c6c6", borderRadius: 6, padding: "0.6rem 0.9rem", marginBottom: "1rem", fontSize: "0.9rem", color: "#b71c1c" }}>
                                            {cadastroErrors._global}
                                        </div>
                                    )}
                                    {data.campos.map((campo) => (
                                        <CampoField
                                            key={campo.nome}
                                            campo={campo}
                                            value={cadastroData[campo.nome] ?? ""}
                                            onChange={(v) => handleCadastroChange(campo.nome, v)}
                                            idPrefix="cad-"
                                            error={cadastroErrors[campo.nome]}
                                        />
                                    ))}
                                    {data.termos_url && (
                                        <TermosCheckbox
                                            id="cad-aceite-termos"
                                            termosUrl={data.termos_url}
                                            checked={cadastroAceiteTermos}
                                            onChange={handleCadastroAceiteTermosChange}
                                            error={cadastroErrors._termos}
                                        />
                                    )}
                                    <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
                                        <button
                                            type="button"
                                            onClick={() => setCadastroOpen(false)}
                                            style={{ flex: 1, padding: "0.7rem", background: "#e2e8f0", color: "#1e293b", border: "none", borderRadius: 8, fontSize: "1rem", cursor: "pointer" }}
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={mutCadastro.isLoading}
                                            style={{ flex: 2, padding: "0.7rem", background: primary, color: "#fff", border: "none", borderRadius: 8, fontSize: "1rem", fontWeight: 600, cursor: mutCadastro.isLoading ? "not-allowed" : "pointer", opacity: mutCadastro.isLoading ? 0.7 : 1 }}
                                        >
                                            {mutCadastro.isLoading ? "Aguarde…" : "Cadastrar"}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── Export envolvido com QueryClientProvider (página pública sem AppProvider) ─

export default function PortalPage() {
    return (
        <QueryClientProvider client={queryClient}>
            <PortalContent />
        </QueryClientProvider>
    );
}
